import { collection, addDoc } from 'firebase/firestore';
import { db, shouldUseLocalStorage } from '../firebase';
import config from '../config';
import { ResultData, SurveyResponse, Description } from '../types';
import { getZodiacSign } from './zodiac';
import { isDevelopment } from './env';

// Save state management using singleton pattern
class SaveStateManager {
  private static instance: SaveStateManager;
  private isSaving: boolean = false;

  private constructor() {}

  public static getInstance(): SaveStateManager {
    if (!SaveStateManager.instance) {
      SaveStateManager.instance = new SaveStateManager();
    }
    return SaveStateManager.instance;
  }

  public reset(): void {
    console.log('=== RESETTING SAVE STATE ===');
    this.isSaving = false;
  }

  public canSave(): boolean {
    return !this.isSaving;
  }

  public startSave(): void {
    this.isSaving = true;
  }

  public endSave(): void {
    this.isSaving = false;
  }
}

// Get singleton instance
const saveState = SaveStateManager.getInstance();

// Validate survey responses
const validateSurveyResponses = (responses: SurveyResponse): { isValid: boolean; missingFields: string[] } => {
  const requiredFields = [
    'selectedDescription',
    'birthday',
    'beliefLevel',
    'knowledgeLevel',
    'horoscopeReading',
    'astrologicalInfluence'
  ];

  const missingFields = requiredFields.filter(field => {
    const value = responses[field as keyof SurveyResponse];
    const isEmpty = !value || value === '' || value === 'Not provided' || value === null || value === undefined;
    if (isEmpty) {
      console.log(`Missing or invalid field: ${field}, value:`, value);
    }
    return isEmpty;
  });

  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};

// Create result object
const createResultObject = (
  responses: SurveyResponse,
  realDescriptions: Description[],
  fakeDescriptions: Description[]
): ResultData | null => {
  const birthday = localStorage.getItem('participantBirthday');
  if (!birthday) {
    console.error('Birthday is missing');
    return null;
  }

  const calculatedSign = getZodiacSign(birthday).toLowerCase();
  const selectedDesc = [...realDescriptions, ...fakeDescriptions].find(d => d.id === responses.selectedDescription);
  
  if (!selectedDesc) {
    console.error('Selected description not found for id:', responses.selectedDescription);
    return null;
  }

  return {
    id: `result_${Date.now()}`,
    timestamp: Date.now(),
    participantName: localStorage.getItem('participantName') || 'Anonymous',
    birthday: birthday,
    selectedDescription: responses.selectedDescription,
    selectedIsReal: selectedDesc.isReal,
    actualSign: calculatedSign,
    isMatch: responses.selectedDescription === calculatedSign,
    beliefLevel: responses.beliefLevel,
    knowledgeLevel: responses.knowledgeLevel,
    horoscopeReading: responses.horoscopeReading,
    astrologicalInfluence: responses.astrologicalInfluence
  };
};

// Save to storage (localStorage or Firebase)
const saveToStorage = async (result: ResultData): Promise<boolean> => {
  try {
    if (shouldUseLocalStorage()) {
      // Development mode: use localStorage
      const existingData = JSON.parse(localStorage.getItem('zodiacResultsData') || '[]');
      localStorage.setItem('zodiacResultsData', JSON.stringify([...existingData, result]));
      console.log('Result saved to localStorage (development mode)');
      return true;
    } else {
      // Production mode: use Firebase only
      if (!db) {
        console.error('Firebase is not initialized');
        return false;
      }
      const collectionName = config.app.resultsCollection;
      await addDoc(collection(db, collectionName), result);
      console.log('Result saved to Firestore (production mode)');
      return true;
    }
  } catch (error) {
    console.error('Error saving result:', error);
    return false;
  }
};

// Main save function
export const saveSurvey = async (
  surveyResponses: SurveyResponse,
  realDescriptions: Description[],
  fakeDescriptions: Description[]
): Promise<boolean> => {
  console.log('=== SAVE SURVEY CALLED ===');
  console.log('Survey responses:', surveyResponses);
  console.log('Environment:', isDevelopment() ? 'Development' : 'Production');
  console.log('Using storage:', shouldUseLocalStorage() ? 'localStorage' : 'Firestore');
  console.log('Firebase initialized:', !!db);

  // Validate responses
  const { isValid, missingFields } = validateSurveyResponses(surveyResponses);
  if (!isValid) {
    console.error('Cannot save: Missing or invalid survey responses:', missingFields);
    return false;
  }

  // Create result object
  const result = createResultObject(surveyResponses, realDescriptions, fakeDescriptions);
  if (!result) {
    console.error('Failed to create result object');
    return false;
  }

  // Save to storage
  try {
    saveState.startSave();
    console.log('Starting save operation with result:', result);
    const success = await saveToStorage(result);
    console.log('Save operation result:', success ? 'Success' : 'Failed');
    return success;
  } finally {
    saveState.endSave();
    console.log('=== SAVE OPERATION COMPLETE ===');
  }
};

// Export reset function
export const resetSaveState = () => {
  saveState.reset();
}; 